import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react-lite';

import useForm from '../hooks/useForm';
import QuestionMultiInput from '../components/QuestionMultiInput';


function QuestionNames({
    firstName,
    middleName,
    lastName,
    onChange,
    children,
    ...otherProps
}) {
    const form = useForm();
    const nameFields = getNameFields({ firstName, middleName, lastName });


    // as-you-type character inspection
    function handleNameChange(evt) {
        const theField = form.getFieldNamed(evt.target.name);
        const acceptableNameCharacters = theField.value
            // rejects em-dash, en-dash, digits, common punctuation and leading spaces; allows hyphen
            // eslint-disable-next-line no-irregular-whitespace
            ?.replace(/[—–0-9~`!@#$%^&*()_+{}".,:;=<>?[\]\\|\\/\\ ]|^ /, '')
            ?.replace(/ {2}/, ' ');     // changes any two spaces to a single

        theField.setValue(acceptableNameCharacters);

        // if the optional onChange() is provided, run it now for customized name filtering
        onChange?.(theField.value, theField);
    }

    return (
        <QuestionMultiInput
            fields={nameFields}
            hideLabels={true}
            labelMessage={(
                <FormattedMessage
                    id="base-ui.names.what-is-your-name.label"
                    defaultMessage="What is your name?"
                />
            )}
            onChange={handleNameChange}
            {...otherProps}
        >
            {children}
        </QuestionMultiInput>
    );
}

QuestionNames.propTypes = {
    firstName: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.string,
        minimumLength: PropTypes.number,
        maximumLength: PropTypes.number,
        label: PropTypes.node,
    }),
    middleName: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.string,
        isRequired: PropTypes.bool,
        minimumLength: PropTypes.number,
        maximumLength: PropTypes.number,
        label: PropTypes.node,
    }),
    lastName: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.string,
        minimumLength: PropTypes.number,
        maximumLength: PropTypes.number,
        label: PropTypes.node,
    }),

    onChange: PropTypes.func,
    children: PropTypes.node,
};

QuestionNames.defaultProps = {
    firstName: undefined,
    middleName: undefined,
    lastName: undefined,

    onChange: undefined,
    children: undefined,
};

const MIN_LENGTH_NAME = 2;
const MAX_LENGTH_NAME = 70;


/**
 * get the `fields` prop for QuestionMultiInput for first, last, and optional middle name values
 *
 * @param {Object} firstName - first name value and options
 * @param {string} [firstName.value] - current value of first name
 * @param {number} [firstName.minimumLength] - minimum name length
 * @param {number} [firstName.maximumLength] - maximum name length
 * @param {Object} [firstName.label] - placeholder text, e.g. (<FormattedMessage … />)
 *
 * @param {Object} [middleName] - middle name value and options
 * @param {boolean} [middleName.isRequired] - required?
 * @param {string} [middleName.value] - current value of middle name
 * @param {number} [middleName.minimumLength] - minimum name length
 * @param {number} [middleName.maximumLength] - maximum name length
 * @param {Object} [middleName.label] - placeholder text, e.g. (<FormattedMessage … />)
 *
 * @param {Object} lastName - last name value and options
 * @param {string} [lastName.value] - current value of last name
 * @param {number} [lastName.minimumLength] - minimum name length
 * @param {number} [lastName.maximumLength] - maximum name length
 * @param {Object} [lastName.label] - placeholder text, e.g. (<FormattedMessage … />)
 *
 * @return {Object}
 */
function getNameFields({
    firstName,
    middleName,
    lastName,
}) {
    const firstNameValidators = useMemo(
        () => [
            createMinimumLengthValidator(firstName.minimumLength),
            createMaximumLengthValidator(firstName.maximumLength),
        ],
        [ firstName.minimumLength, firstName.maximumLength ],
    );

    const middleNameValidators = useMemo(
        () => [
            createMinimumLengthValidator(middleName?.minimumLength),
            createMaximumLengthValidator(middleName?.maximumLength),
        ],
        [ middleName?.minimumLength, middleName?.maximumLength ],
    );

    const lastNameValidators = useMemo(
        () => [
            createMinimumLengthValidator(lastName.minimumLength),
            createMaximumLengthValidator(lastName.maximumLength),
        ],
        [ lastName.minimumLength, lastName.maximumLength ],
    );

    const nameFields = [
        {
            field: {
                name: firstName.name ?? 'firstName',
                value: firstName.value,
                type: 'string',
                validators: firstNameValidators,
            },
            label: firstName?.label ?? (
                <FormattedMessage
                    id="base-ui.names.first-name.label"
                    defaultMessage="First name"
                />
            ),
        },
        {
            field: {
                name: lastName.name ?? 'lastName',
                value: lastName.value,
                type: 'string',
                validators: lastNameValidators,
            },
            label: lastName?.label ?? (
                <FormattedMessage
                    id="base-ui.names.last-name.label"
                    defaultMessage="Last name"
                />
            ),
        },
    ];

    if (middleName) {
        const isRequired = middleName?.isRequired ?? true;

        nameFields.splice(1, -0, {
            field: {
                name: middleName.name ?? 'middleName',
                value: middleName?.value,
                type: 'string',
                required: isRequired,
                validators: middleNameValidators,
            },
            label: middleName?.label ?? (
                <FormattedMessage
                    id="base-ui.names.middle-name.label"
                    defaultMessage="Middle name{isRequired, select, true {} other { (optional)}}"
                    values={{ isRequired: isRequired }}
                />
            ),
        });
    }

    return nameFields;
}


function createMinimumLengthValidator(minimumLength = MIN_LENGTH_NAME) {
    return {
        fn: name => name.trim().length >= minimumLength,
        message: (
            <FormattedMessage
                id="base-ui.names.validation.minimum-length"
                defaultMessage="This name must be at least {minimumLength, plural, one {# character} other {# characters}} long."
                values={{
                    minimumLength: minimumLength,
                }}
            />
        ),
        key: 'minimum-name-length',
    };
}

function createMaximumLengthValidator(maximumLength = MAX_LENGTH_NAME) {
    return {
        fn: name => name.trim().length <= maximumLength,
        message: (
            <FormattedMessage
                id="base-ui.names.validation.maximum-length"
                defaultMessage="This name must not be longer than {maximumLength} characters."
                values={{
                    maximumLength: maximumLength,
                }}
            />
        ),
        key: 'minimum-name-length',
    };
}


export default observer(QuestionNames);
